import { useState } from 'react';
import { Box, Button, Link, List, ListItem, Typography } from '@mui/material';
import { ListItemStyle, Label, LinkWrapper, signedOffMobileMenuHeader } from './MobileList.styles';
import { Logout } from '../Logout/Logout';
import LogoutMobile from '@/assets/images/logout-mobile.svg';
import { Stack } from '@mui/material';
import { AccountPhoto } from '@barracuda/shared/components/AccountPhoto/AccountPhoto';
import { useAppContext } from '@/context/App.context';
import Img from '@barracuda/shared/components/Primitive/Img';
import { useTranslation } from 'react-i18next';
import { LocalizedNavLink } from '../LocalizedNavLink/LocalizedNavLink';
import { theme } from '@theme';
import {
  logoLink,
  signInButtonNavbar,
  signUpButtonNavbar
} from '../HeaderNavbar/HeaderNavbar.style';
import Logo from '@/assets/images/pearson-logo-odyssey.svg';
import MenuExitIcon from '@/assets/images/menu__exit.svg';
import { sideBarButtons } from '../SideBarNavigation/SideBarNavigation.styles';
import {
  MenuItemType,
  MenuSubItemType,
  renderMobileItems
} from '../NavbarMenuItem/navbarMenu.utils';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';

export interface MobileListItemInterface {
  link: string;
  label: string;
  replace?: boolean;
  small?: boolean;
}

export interface MobileListProps {
  (items: MobileListItemInterface[], setIsMenuOpen?: (value: boolean) => void):
    | JSX.Element
    | JSX.Element[];
}

export interface MobileListItemProps {
  (
    item: MobileListItemInterface,
    index: number | string,
    setIsMenuOpen?: (value: boolean) => void,
    style?: Record<string, any>
  ): JSX.Element;
}

export const MobileListItem: MobileListItemProps = (item, index, setIsMenuOpen, style = {}) => (
  <LocalizedNavLink
    style={({ isActive }) => ({ ...LinkWrapper(isActive && !item.replace, item.small), ...style })}
    end
    to={item.replace ? '' : item.link}
    key={`navLink-${index}`}
    onClick={() => (item.replace ? window.open(item.link) : setIsMenuOpen?.(false))}
  >
    <ListItem sx={ListItemStyle}>
      <Typography sx={Label(item.small)}>{item.label}</Typography>
    </ListItem>
  </LocalizedNavLink>
);

export const MobileList: MobileListProps = (items, setIsMenuOpen) => {
  const { user, isLoggedIn } = useAppContext();
  const { t } = useTranslation();
  const [activeItems, setActiveItems] = useState<MenuItemType[]>([]);
  const navigate = useLocalizedNavigate();
  const listItems = items.map((item, index) => MobileListItem(item, index, setIsMenuOpen));

  const handleItemClick = (item: MenuItemType | MenuSubItemType) => {
    if ('items' in item && item?.items?.length) {
      if (activeItems.includes(item)) {
        setActiveItems(activeItems.filter((arrayItem) => item !== arrayItem));
      } else {
        setActiveItems([...activeItems, item]);
      }
    } else {
      if ('target' in item) {
        window.open(item.link, item.target);
      } else {
        navigate(item.link as string);
      }
      handleClose();
    }
  };

  const handleClose = () => {
    setActiveItems([]);
    setIsMenuOpen?.(false);
  };

  const handleSignUp = () => {
    navigate('/join');
    handleClose();
  };

  const handleSignIn = () => {
    navigate('/login');
    handleClose();
  };

  if (isLoggedIn) {
    listItems.push(
      <ListItem key='navLink-account' style={{ marginTop: '30px' }}>
        <LocalizedNavLink
          to='/login?redirectUrl=/dashboard/account'
          style={{ textDecoration: 'none' }}
          onClick={() => setIsMenuOpen?.(false)}
        >
          <Stack direction='row'>
            <AccountPhoto imageUri={user?.imageUri} width='24px' height='24px' />
            <Typography
              sx={{
                marginLeft: '5px',
                fontSize: '16px',
                color: theme.palette.common.darkGrey2,
                textDecoration: 'none'
              }}
            >
              {t('menuLabels.account')}
            </Typography>
          </Stack>
        </LocalizedNavLink>
      </ListItem>
    );

    listItems.push(
      <ListItem key='navLink-logout' onClick={() => setIsMenuOpen?.(false)} sx={{ padding: 0 }}>
        <Logout sx={{ flexGrow: 1, padding: '8px 16px' }}>
          <Stack direction='row'>
            <Img src={LogoutMobile} alt='logout' />
            <Typography
              sx={{
                marginLeft: '5px',
                fontSize: '16px',
                color: theme.palette.common.darkGrey2,
                textDecoration: 'none'
              }}
            >
              {t('menuLabels.logOut')}
            </Typography>
          </Stack>
        </Logout>
      </ListItem>
    );
  } else {
    return (
      <Stack>
        <Box sx={signedOffMobileMenuHeader}>
          <Link onClick={() => navigate('/')} sx={logoLink}>
            <Img src={Logo} alt='PTE Express logo' />
          </Link>
          <Img src={MenuExitIcon} onClick={handleClose} />
        </Box>
        <Stack sx={sideBarButtons}>
          <Button sx={signUpButtonNavbar} onClick={handleSignUp} id='header-sign-up'>
            <Box display='flex' gap='8px' alignItems='center'>
              <Box>{t('signUpText')}</Box>
            </Box>
          </Button>
          <Button size='large' sx={signInButtonNavbar} onClick={handleSignIn} id='header-sign-in'>
            {t('loginInText')}
          </Button>
        </Stack>
        <List component='nav'>{renderMobileItems(activeItems, handleItemClick)}</List>
      </Stack>
    );
  }

  return listItems;
};
